// ============================================================
// src/pages/MechanicsPage.jsx  [Supervisor]
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getEmployees, getDepartments, createEmployee, updateEmployee, resetPassword, toggleLock } from '../api/employeeApi';
import { toDisplay, toInputValue } from '../utils/dateHelper';
import { Toast, Btn, Field, Input, Select, Modal, Spinner } from '../components/UI';
import { useToast } from '../hooks/useToast';
import { roleFromDeptCode } from '../utils/roles';

const EMPLOYEE_DEPT_CODES = ['ADMIN', 'FLEET', 'SHOP', 'WELDER'];

const EMPTY = {
  ee_number: '', first_name: '', last_name: '', email: '', phone: '',
  dept_code: 'SHOP', hire_date: '', hourly_rate: '', username: '', password: '',
};

// ── Helpers ───────────────────────────────────────────────────
function generatePassword(len = 10) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789#@!';
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function generateUsername(firstName, lastName) {
  const f = (firstName || '').trim().toLowerCase().replace(/\s/g, '');
  const l = (lastName || '').trim().toLowerCase().replace(/\s/g, '');
  return (f[0] || '') + l;
}

// ── Stat Card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'gray' }) {
  const borderColors = { gray: '#9ca3af', green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
  const valueColors = { gray: '#111827', green: '#16a34a', yellow: '#d97706', red: '#dc2626' };
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb',
      borderTop: `3px solid ${borderColors[color]}`,
      borderRadius: '6px', padding: '1.1rem 1.4rem',
      boxShadow: '0 1px 3px rgba(0,0,0,.06)', flex: 1, minWidth: '160px',
    }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '.06em', color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.4rem', whiteSpace: 'nowrap' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: valueColors[color], lineHeight: 1.1, marginBottom: '0.25rem' }}>
        {value}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{sub}</div>
    </div>
  );
}

// ── Section header (red underline) ────────────────────────────
function SectionHeader({ title }) {
  return (
    <div style={{ marginBottom: '1rem', marginTop: '0.25rem' }}>
      <div style={{
        fontSize: '0.72rem', fontWeight: 700, color: '#dc2626',
        letterSpacing: '.08em', textTransform: 'uppercase',
        paddingBottom: '0.4rem', borderBottom: '2px solid #dc2626',
      }}>
        {title}
      </div>
    </div>
  );
}

// ── Modal field with label ────────────────────────────────────
function MField({ label, children, span2 = false }) {
  return (
    <div style={{ gridColumn: span2 ? 'span 2' : 'span 1' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid #d1d5db', borderRadius: '4px',
  padding: '0.5rem 0.7rem', fontSize: '0.875rem',
  color: '#111827', background: '#f9fafb', outline: 'none',
};
const readonlyStyle = { ...inputStyle, background: '#f3f4f6', color: '#9ca3af' };
const autoBtn = {
  padding: '0.5rem 0.8rem', fontSize: '0.78rem', fontWeight: 700,
  border: '1px solid #d1d5db', borderRadius: '4px',
  background: '#f3f4f6', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap',
};
const genBtn = { ...autoBtn, minWidth: '90px' };

export default function MechanicsPage() {
  const { toast, showToast, clearToast } = useToast();
  const [mechanics, setMechanics] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [resetTarget, setResetTarget] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [resetting, setResetting] = useState(false);

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const eUpd = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mechData, deptData] = await Promise.all([
        getEmployees(search),
        getDepartments(),
      ]);
      setMechanics(mechData);
      console.log(deptData);
      
      setDepts(deptData.filter(d => EMPLOYEE_DEPT_CODES.includes(d.dept_code)));
    } catch (err) {
      showToast(err.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, showToast]);

  useEffect(() => { fetchData(); }, []);

  // ── Derived KPIs ──────────────────────────────────────────────
  const total = mechanics.length;
  const pending = mechanics.filter(m => m.account_status === 'no-account').length;
  const locked = mechanics.filter(m => m.account_status === 'locked').length;
  const active = mechanics.filter(m => m.account_status === 'active').length;

  // ── Client-side filter ────────────────────────────────────────
  const filtered = mechanics.filter(m => {
    if (!statusFilter) return true;
    if (statusFilter === 'active') return m.account_status === 'active';
    if (statusFilter === 'locked') return m.account_status === 'locked';
    if (statusFilter === 'pending') return m.account_status === 'no-account';
    return true;
  });
  // ── Add mechanic ──────────────────────────────────────────────
  async function handleAdd(e) {
    e.preventDefault();
    if (!form.ee_number.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      showToast('EE#, First Name and Last Name are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await createEmployee({
        ...form,
        ee_number: form.ee_number.trim().toUpperCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        username: form.username.trim().toLowerCase() || null,
        password: form.password.trim() || null,
        hire_date: form.hire_date ? toDisplay(form.hire_date) : null,
        hourly_rate: form.hourly_rate || null,
        role: roleFromDeptCode(form.dept_code),
      });
      showToast('Mechanic added ✓', 'success');
      setAddOpen(false);
      setForm(EMPTY);
      fetchData();
    } catch (err) {
      showToast(err.message || 'Failed to create', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── Edit mechanic ─────────────────────────────────────────────
  async function handleEdit(e) {
    e.preventDefault();
    if (!editForm.first_name?.trim() || !editForm.last_name?.trim()) {
      showToast('First Name and Last Name are required', 'error');
      return;
    }
    setEditSaving(true);
    try {
      await updateEmployee(editTarget.id, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: (editForm.email || '').trim() || null,
        phone: (editForm.phone || '').trim() || null,
        hire_date: editForm.hire_date ? toDisplay(editForm.hire_date) : null,
        hourly_rate: editForm.hourly_rate || null,
        dept_code: editForm.dept_code || 'SHOP',
        role: roleFromDeptCode(editForm.dept_code),
        username: (editForm.username || '').trim().toLowerCase() || null,
        password: (editForm.password || '').trim() || null,
      });
      showToast('Updated ✓', 'success');
      setEditTarget(null);
      fetchData();
    } catch (err) {
      showToast(err.message || 'Failed to update', 'error');
    } finally {
      setEditSaving(false);
    }
  }

  // ── Reset password ────────────────────────────────────────────
  async function handleReset(e) {
    e.preventDefault();
    if (!newPass.trim()) { showToast('Enter a new password', 'error'); return; }
    setResetting(true);
    try {
      await resetPassword(resetTarget.id, newPass.trim());
      showToast('Password reset ✓', 'success');
      setResetTarget(null);
      setNewPass('');
    } catch (err) {
      showToast(err.message || 'Reset failed', 'error');
    } finally {
      setResetting(false);
    }
  }

  // ── Toggle lock ───────────────────────────────────────────────
  async function handleToggleLock(emp) {
    const isLocked = emp.account_status === 'locked';
    if (!window.confirm(`${isLocked ? 'Unlock' : 'Lock'} account for ${emp.first_name} ${emp.last_name}?`)) return;
    try {
      await toggleLock(emp.id, !isLocked);
      showToast(`Account ${isLocked ? 'unlocked' : 'locked'} ✓`, isLocked ? 'success' : 'warning');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Failed', 'error');
    }
  }

  // ── Open edit ─────────────────────────────────────────────────
  function openEdit(emp) {
    setEditTarget(emp);
    setEditForm({
      ee_number: emp.ee_number,
      first_name: emp.first_name,
      last_name: emp.last_name,
      email: emp.email || '',
      phone: emp.phone || '',
      hire_date: toInputValue(emp.hire_date),
      hourly_rate: emp.hourly_rate,
      dept_code: emp.dept_code || 'SHOP',
      dept_name: emp.dept_name || '',
      username: emp.username || '',
      password: '',
    });
  }

  // ── Table button styles ───────────────────────────────────────
  const btnBase = { padding: '0.28rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' };
  const btnEdit = { ...btnBase, border: '1px solid #d1d5db', background: '#fff', color: '#374151' };
  const btnReset = { ...btnBase, border: '1px solid #f59e0b', background: '#fffbeb', color: '#b45309' };
  const btnLock = { ...btnBase, border: '1px solid #dc2626', background: '#fef2f2', color: '#dc2626' };
  const btnUnlock = { ...btnBase, border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a' };

  return (
    <div style={{ padding: '1.5rem', background: '#f3f4f6', minHeight: '100vh' }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0, letterSpacing: '.02em', color: '#111827' }}>
            MECHANICS
          </h1>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Add mechanics, assign login credentials, manage accounts
          </p>
        </div>
        <button onClick={() => { setForm(EMPTY); setAddOpen(true); }} style={{
          background: '#dc2626', color: '#fff', border: 'none',
          padding: '0.6rem 1.25rem', borderRadius: '5px',
          fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          boxShadow: '0 1px 3px rgba(0,0,0,.15)',
        }}>
          + ADD MECHANIC
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <StatCard label="Total Mechanics" value={total} sub="All employees" color="gray" />
        <StatCard label="Active Accounts" value={active} sub="Can login" color="green" />
        <StatCard label="Pending Setup" value={pending} sub="No credentials yet" color="yellow" />
        <StatCard label="Locked Out" value={locked} sub="Failed attempts" color="red" />
      </div>

      {/* ── Filter Row ── */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px',
        padding: '1rem 1.25rem', marginBottom: '1.25rem',
        display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Search</div>
          <input type="text" style={{ ...inputStyle, background: '#fff' }} value={search}
            onChange={e => setSearch(e.target.value)} placeholder="Name or EE#…"
            onKeyDown={e => e.key === 'Enter' && fetchData()} />
        </div>
        <div style={{ minWidth: '140px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '0.3rem' }}>Status</div>
          <select style={{ ...inputStyle, background: '#fff', width: 'auto', minWidth: '140px' }}
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="locked">Locked</option>
            <option value="pending">Pending Setup</option>
          </select>
        </div>
        <button onClick={fetchData} style={{
          background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db',
          padding: '0.5rem 1rem', borderRadius: '4px',
          fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap',
        }}>
          🔍 SEARCH
        </button>
      </div>

      {/* ── Table ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '2px solid #dc2626' }}>
          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#dc2626', letterSpacing: '.06em', textTransform: 'uppercase' }}>
            Mechanic Accounts
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['EE#', 'Last Name', 'First Name', 'Department', 'Role', 'Username', 'Account Status', 'Last Login', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No mechanics found.</td></tr>
                ) : filtered.map(m => (
                  <tr key={m.id}
                    style={{ borderBottom: '1px solid #f3f4f6', background: '#fff' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>

                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', color: '#dc2626', fontWeight: 600 }}>{m.ee_number}</td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#111827' }}>{m.last_name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{m.first_name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{m.dept_name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280', textTransform: 'capitalize' }}>{m.role}</td>
                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', color: '#6b7280' }}>
                      {m.username || <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>not set</span>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {m.account_status === 'active'
                        ? <span style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>ACTIVE</span>
                        : m.account_status === 'locked'
                          ? <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>LOCKED</span>
                          : <span style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', borderRadius: '4px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>PENDING SETUP</span>
                      }
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                      {m.last_login_at || '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap' }}>
                        <button style={btnEdit} onClick={() => openEdit(m)}>✏ EDIT</button>
                        <button style={btnReset} onClick={() => { setResetTarget(m); setNewPass(''); }}>🔑 RESET PW</button>
                        {m.account_status === 'locked'
                          ? <button style={btnUnlock} onClick={() => handleToggleLock(m)}>🔓 UNLOCK</button>
                          : <button style={btnLock} onClick={() => handleToggleLock(m)}>🔒 LOCK</button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          ADD MECHANIC MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal open={addOpen} title="ADD NEW MECHANIC" onClose={() => setAddOpen(false)}
        footer={<>
          <Btn variant="secondary" onClick={() => setAddOpen(false)}>CANCEL</Btn>
          <Btn onClick={handleAdd} disabled={saving}>{saving ? 'SAVING…' : '💾 SAVE MECHANIC'}</Btn>
        </>}>
        <form onSubmit={handleAdd}>

          <SectionHeader title="Employee Information" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '1.5rem' }}>
            <MField label="EE # (Employee ID)">
              <input style={inputStyle} type="text" value={form.ee_number}
                onChange={upd('ee_number')} placeholder="e.g. 5801" required />
            </MField>
            <MField label="Department">
              <select style={inputStyle} value={form.dept_code} onChange={upd('dept_code')}>
                {depts.map(d => <option key={d.dept_code} value={d.dept_code}>{d.dept_name}</option>)}
              </select>
            </MField>
            <MField label="First Name">
              <input style={inputStyle} type="text" value={form.first_name}
                onChange={upd('first_name')} placeholder="First name" required />
            </MField>
            <MField label="Last Name">
              <input style={inputStyle} type="text" value={form.last_name}
                onChange={upd('last_name')} placeholder="Last name" required />
            </MField>
            <MField label="Email (Optional)">
              <input style={inputStyle} type="email" value={form.email}
                onChange={upd('email')} placeholder="email@fmcc.com" />
            </MField>
            <MField label="Phone (Optional)">
              <input style={inputStyle} type="text" value={form.phone}
                onChange={upd('phone')} placeholder="(239) 555-0000" />
            </MField>
            <MField label="Hire Date">
              <input style={inputStyle} type="date" value={form.hire_date}
                onChange={upd('hire_date')} />
            </MField>
            <MField label="Hourly Rate">
              <input style={inputStyle} type="number" step="0.01" value={form.hourly_rate}
                onChange={upd('hourly_rate')} placeholder="22.50" />
            </MField>
          </div>

          {/* Credentials are OPTIONAL — mechanic can be added without them */}
          <SectionHeader title="Login Credentials (Optional)" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <MField label="Username">
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input style={{ ...inputStyle, flex: 1 }} type="text" value={form.username}
                  onChange={upd('username')} placeholder="e.g. elopez" />
                <button type="button" style={autoBtn}
                  onClick={() => setForm(f => ({ ...f, username: generateUsername(f.first_name, f.last_name) }))}>
                  AUTO
                </button>
              </div>
            </MField>
            <MField label="Temporary Password">
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input style={{ ...inputStyle, flex: 1 }} type="text" value={form.password}
                  onChange={upd('password')} placeholder="Min 8 characters" />
                <button type="button" style={genBtn}
                  onClick={() => setForm(f => ({ ...f, password: generatePassword() }))}>
                  GENERATE
                </button>
              </div>
            </MField>
          </div>

          {/* Info note */}
          <div style={{ marginTop: '0.85rem', padding: '0.6rem 0.85rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '4px', fontSize: '0.78rem', color: '#92400e' }}>
            ⚠ If credentials are left blank, this mechanic will appear under <strong>Pending Setup</strong> and cannot log in until credentials are assigned.
          </div>
        </form>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          EDIT MECHANIC MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal open={!!editTarget}
        title={`EDIT MECHANIC — ${editTarget?.first_name} ${editTarget?.last_name}`}
        onClose={() => setEditTarget(null)}
        footer={<>
          <Btn variant="secondary" onClick={() => setEditTarget(null)}>CANCEL</Btn>
          <Btn onClick={handleEdit} disabled={editSaving}>{editSaving ? 'SAVING…' : '💾 SAVE MECHANIC'}</Btn>
        </>}>
        <form onSubmit={handleEdit}>

          <SectionHeader title="Employee Information" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '1.5rem' }}>
            <MField label="EE # (Employee ID)">
              <input style={readonlyStyle} type="text" value={editForm.ee_number || ''} readOnly />
            </MField>
            <MField label="Department">
              <select style={inputStyle} value={editForm.dept_code || 'SHOP'} onChange={eUpd('dept_code')}>
                {depts.map(d => <option key={d.dept_code} value={d.dept_code}>{d.dept_name}</option>)}
              </select>
            </MField>
            <MField label="First Name">
              <input style={inputStyle} type="text" value={editForm.first_name || ''}
                onChange={eUpd('first_name')} required />
            </MField>
            <MField label="Last Name">
              <input style={inputStyle} type="text" value={editForm.last_name || ''}
                onChange={eUpd('last_name')} required />
            </MField>
            <MField label="Email (Optional)">
              <input style={inputStyle} type="email" value={editForm.email || ''}
                onChange={eUpd('email')} placeholder="email@fmcc.com" />
            </MField>
            <MField label="Phone (Optional)">
              <input style={inputStyle} type="text" value={editForm.phone || ''}
                onChange={eUpd('phone')} placeholder="(239) 555-0000" />
            </MField>
            <MField label="Hire Date">
              <input style={inputStyle} type="date" value={editForm.hire_date || ''}
                onChange={eUpd('hire_date')} />
            </MField>
            <MField label="Hourly Rate">
              <input style={inputStyle} type="number" step="0.01" value={editForm.hourly_rate || ''}
                onChange={eUpd('hourly_rate')} />
            </MField>
          </div>

          <SectionHeader title="Login Credentials" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <MField label="Username">
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input style={{ ...inputStyle, flex: 1 }} type="text" value={editForm.username || ''}
                  onChange={eUpd('username')} placeholder="e.g. elopez" />
                <button type="button" style={autoBtn}
                  onClick={() => setEditForm(f => ({ ...f, username: generateUsername(f.first_name, f.last_name) }))}>
                  AUTO
                </button>
              </div>
            </MField>
            <MField label="Temporary Password">
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input style={{ ...inputStyle, flex: 1 }} type="text" value={editForm.password || ''}
                  onChange={eUpd('password')} placeholder="Leave blank to keep current" />
                <button type="button" style={genBtn}
                  onClick={() => setEditForm(f => ({ ...f, password: generatePassword() }))}>
                  GENERATE
                </button>
              </div>
            </MField>
          </div>

          <div style={{ marginTop: '0.85rem', padding: '0.6rem 0.85rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '4px', fontSize: '0.78rem', color: '#0369a1' }}>
            ℹ Leave password blank to keep the current password unchanged.
          </div>
        </form>
      </Modal>

      {/* ── Reset Password Modal ── */}
      <Modal open={!!resetTarget}
        title={`Reset Password — ${resetTarget?.last_name}, ${resetTarget?.first_name}`}
        onClose={() => setResetTarget(null)}
        footer={<>
          <Btn variant="secondary" onClick={() => setResetTarget(null)}>CANCEL</Btn>
          <Btn onClick={handleReset} disabled={resetting}>{resetting ? 'RESETTING…' : 'RESET PASSWORD'}</Btn>
        </>}>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Enter a new password for <strong>{resetTarget?.first_name} {resetTarget?.last_name}</strong>.
        </p>
        <MField label="New Password">
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input style={{ ...inputStyle, flex: 1 }} type="text" value={newPass}
              onChange={e => setNewPass(e.target.value)} placeholder="NewPass123" autoFocus />
            <button type="button" style={genBtn} onClick={() => setNewPass(generatePassword())}>
              GENERATE
            </button>
          </div>
        </MField>
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}